import { useEffect, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import {
  healthDisplay, healthStatusFromError, kpiCoverageLabel, hasAttendanceData, hasKpiData,
} from "@/lib/dashboard-utils";
import { PageHeader } from "./AdminLayout";
import {
  Users, Building2, Building, ClipboardList, AlertTriangle, CheckCircle2,
  CalendarCheck, TrendingUp, Activity, Sparkles
} from "lucide-react";

const STATUS_LABEL = {
  open: "Mở", in_progress: "Đang làm", submitted: "Chờ duyệt",
  approved: "Đã duyệt", rejected: "Trả lại", overdue: "Quá hạn", closed: "Đóng"
};
const STATUS_COLOR = {
  open: "bg-stone-200 text-stone-800",
  in_progress: "bg-blue-100 text-blue-900",
  submitted: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-900",
  rejected: "bg-red-100 text-red-900",
  closed: "bg-stone-200 text-stone-700",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthStatus, setHealthStatus] = useState(null);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api
      .get("/health/ready")
      .then((r) => setHealthStatus(r.data?.status || "ok"))
      .catch((err) => setHealthStatus(healthStatusFromError(err)));
  }, []);

  if (loading || !data) {
    return (
      <div data-testid="admin-dashboard">
        <PageHeader kicker="Holding Overview" title="Toàn cảnh điều hành" breadcrumb={["Thanh Sơn ERP", "Dashboard"]} />
        <div className="p-6 lg:p-8 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array(8).fill(0).map((_, i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-sm p-4 animate-pulse">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-sm bg-stone-200" />
                  <div className="h-2.5 bg-stone-200 rounded w-20" />
                </div>
                <div className="h-8 bg-stone-200 rounded w-16 mt-2" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-stone-200 rounded-sm h-64 animate-pulse" />
            <div className="bg-white border border-stone-200 rounded-sm h-64 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const s = data.stats;
  const kpiHasData = hasKpiData(s);
  const attHasData = hasAttendanceData(s);
  const health = healthDisplay(healthStatus);
  return (
    <div data-testid="admin-dashboard">
      <PageHeader
        kicker="Holding Overview"
        title="Toàn cảnh điều hành"
        breadcrumb={["Thanh Sơn ERP", "Dashboard"]}
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={Users} label="Nhân sự active" value={s.employees} accent="emerald" testid="stat-employees" />
          <StatTile icon={Building2} label="Pháp nhân" value={s.companies} sub={`${s.branches} chi nhánh`} testid="stat-companies" />
          <StatTile icon={ClipboardList} label="Công việc mở" value={s.task_open} sub={`${s.task_done} đã hoàn thành`} testid="stat-tasks" />
          <StatTile icon={AlertTriangle} label="Quá hạn" value={s.task_overdue} accent={s.task_overdue > 0 ? "red" : ""} testid="stat-overdue" />
          <StatTile
            icon={CalendarCheck}
            label="Check-in hôm nay"
            value={attHasData ? s.att_today : null}
            emptyText="Chưa có dữ liệu"
            testid="stat-checkin"
          />
          <StatTile
            icon={TrendingUp}
            label="KPI TB (đã ghi nhận)"
            value={kpiHasData ? `${s.kpi_avg}` : null}
            suffix="%"
            emptyText="Chưa có dữ liệu"
            sub={kpiHasData ? kpiCoverageLabel(s.kpi_companies, s.companies) : undefined}
            accent="emerald"
            testid="stat-kpi"
          />
          <StatTile icon={CheckCircle2} label="Task duyệt" value={s.task_done} testid="stat-done" />
          <StatTile
            icon={Activity}
            label="Tình trạng hệ thống"
            value={health ? health.label : null}
            emptyText="Đang kiểm tra…"
            accent={health ? health.accent : ""}
            testid="stat-health"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* KPI by company */}
          <div className="lg:col-span-2 bg-white border border-stone-200 rounded-sm">
            <div className="px-5 py-3.5 border-b border-stone-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Roll-up</div>
                <h3 className="font-serif text-lg font-bold mt-0.5">KPI theo pháp nhân</h3>
              </div>
              <Sparkles className="w-4 h-4 text-amber-600" />
            </div>
            <div className="p-5 space-y-3">
              {data.kpi_by_company.map((c) => {
                const score = Number(c.avg_score || 0);
                const recorded = Number(c.kpi_records || 0) > 0;
                return (
                  <div key={c.company}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <div className="text-sm font-semibold text-stone-900">{c.company}</div>
                        <div className="text-xs text-stone-500">{c.employees} nhân sự</div>
                      </div>
                      {recorded ? (
                        <div className="font-serif text-xl font-bold text-stone-900">
                          {score.toFixed(1)}<span className="text-xs text-stone-500 ml-0.5">/100</span>
                        </div>
                      ) : (
                        <div className="text-xs font-semibold text-stone-400">Chưa có dữ liệu</div>
                      )}
                    </div>
                    <div className="h-2 bg-stone-100 rounded-sm overflow-hidden">
                      {recorded && (
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${Math.min(100, score)}%`,
                            background: score >= 90 ? "#0F4D3A" : score >= 70 ? "#C8923C" : "#a8a29e"
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Task status pie-like */}
          <div className="bg-white border border-stone-200 rounded-sm">
            <div className="px-5 py-3.5 border-b border-stone-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Phân bổ</div>
              <h3 className="font-serif text-lg font-bold mt-0.5">Trạng thái công việc</h3>
            </div>
            <div className="p-5 space-y-2">
              {data.task_status.length === 0 && (
                <div className="text-sm text-stone-400 py-6 text-center">Chưa có dữ liệu</div>
              )}
              {data.task_status.map((t) => {
                const total = data.task_status.reduce((a, b) => a + Number(b.n), 0) || 1;
                const pct = (Number(t.n) / total) * 100;
                return (
                  <div key={t.status} className="text-sm">
                    <div className="flex justify-between mb-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded-sm font-semibold ${STATUS_COLOR[t.status] || "bg-stone-100"}`}>
                        {STATUS_LABEL[t.status] || t.status}
                      </span>
                      <span className="font-mono text-stone-600">{t.n}</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-sm overflow-hidden">
                      <div className="h-full bg-emerald-800" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Attendance trend */}
          <div className="bg-white border border-stone-200 rounded-sm lg:col-span-2">
            <div className="px-5 py-3.5 border-b border-stone-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">7 ngày gần nhất</div>
              <h3 className="font-serif text-lg font-bold mt-0.5">Chấm công</h3>
            </div>
            <div className="p-5">
              {!attHasData ? (
                <div className="h-32 flex flex-col items-center justify-center text-center">
                  <div className="text-sm font-semibold text-stone-400">Chưa có dữ liệu chấm công</div>
                  <div className="text-xs text-stone-400 mt-1">Module chấm công chưa ghi nhận lượt check-in nào.</div>
                </div>
              ) : (
                <div className="flex items-end gap-2 h-32">
                  {(data.att_7d.length ? data.att_7d : Array(7).fill({ date: "", n: 0 })).map((d, i) => {
                    const max = Math.max(...data.att_7d.map((x) => Number(x.n)), 1);
                    const h = Math.max(4, (Number(d.n) / max) * 100);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full bg-emerald-800 rounded-sm relative group" style={{ height: `${h}%` }}>
                          <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-stone-700">{d.n}</div>
                        </div>
                        <div className="text-[10px] text-stone-500 font-mono">{d.date?.slice(5) || "—"}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Recent tasks */}
          <div className="bg-white border border-stone-200 rounded-sm">
            <div className="px-5 py-3.5 border-b border-stone-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Mới nhất</div>
              <h3 className="font-serif text-lg font-bold mt-0.5">Công việc gần đây</h3>
            </div>
            <ul className="divide-y divide-stone-100">
              {data.recent_tasks.length === 0 && (
                <li className="px-5 py-6 text-sm text-stone-400 text-center">Chưa có dữ liệu</li>
              )}
              {data.recent_tasks.map((t) => (
                <li key={t.id} className="px-5 py-3">
                  <div className="text-sm font-semibold text-stone-900">{t.title}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-stone-500">{t.assignee || "—"}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${STATUS_COLOR[t.status]}`}>
                      {STATUS_LABEL[t.status]}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub, accent = "", suffix = "", emptyText = "Chưa có dữ liệu", testid }) {
  const isEmpty = value === null || value === undefined || value === "";
  const aColor = isEmpty
    ? "text-stone-400"
    : accent === "emerald"
    ? "text-emerald-800"
    : accent === "amber"
    ? "text-amber-700"
    : accent === "red"
    ? "text-red-700"
    : "text-stone-900";
  const iColor = isEmpty
    ? "bg-stone-100 text-stone-400"
    : accent === "emerald"
    ? "bg-emerald-100 text-emerald-900"
    : accent === "amber"
    ? "bg-amber-100 text-amber-900"
    : accent === "red"
    ? "bg-red-100 text-red-700"
    : "bg-stone-100 text-stone-700";
  return (
    <div className="bg-white border border-stone-200 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${iColor}`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">{label}</div>
      </div>
      {isEmpty ? (
        <div className="font-serif text-base font-semibold text-stone-400 leading-tight mt-1">{emptyText}</div>
      ) : (
        <div className={`font-serif text-3xl font-bold ${aColor} leading-none`}>
          {typeof value === "number" ? fmtNumber(value) : value}<span className="text-base ml-0.5">{suffix}</span>
        </div>
      )}
      {sub && <div className="text-xs text-stone-500 mt-1.5">{sub}</div>}
    </div>
  );
}
